import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, Fab, IconButton, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import SettingsIcon from "@mui/icons-material/Settings"
import moment from "moment"

import { createDeliveries, showDeliveryMenuRecipe } from "../../../actions/Organizations/DeliveryMenus"
import { showEnterprises } from "../../../actions/Organizations/Enterprise"
import {
  getOrganizationsDeliveriesSelector,
  getOrganizationsEnterprisesSelector,
  getAvailableDCsSelector,
  getDeliveriesLoadErrorSelector,
} from "../../../reducers/Organizations/organizations"
import GenericTopBarHeader from "../../../components/GenericTopBarHeader"
import PageHeaderTitle from "../../../components/PageHeaderTitle"
import EnterpriseBadge from "../../../components/Organizations/EnterpriseBadge"
import { COLORS } from "../../../utils"
import DeliveryMenuForm from "./form/DeliveryMenuForm"
import DeliveryMenusFilter from "./DeliveryMenusFilter"

const STATUS_COLUMNS = [
  { key: "DRAFT",          label: "Brouillon",           dot: "#EFAA3E", rgb: "239, 170, 62",  headerBg: "#F5F0EB" },
  { key: "WAITING_CLIENT", label: "En attente client",   dot: "#7C7C7C", rgb: "124, 124, 124", headerBg: "#F5F5F5" },
  { key: "VALIDATED",      label: "Prévisionnel validé", dot: "#2196F3", rgb: "33, 150, 243",  headerBg: "#EBEFF5" },
  { key: "FINAL",          label: "Version définitive",  dot: "#3EA15B", rgb: "62, 161, 91",   headerBg: "#EDF0ED" },
]

const DeliveryMenus = () => {
  const dispatch = useDispatch()
  const deliveries = useSelector(getOrganizationsDeliveriesSelector)
  const enterprises = useSelector(getOrganizationsEnterprisesSelector)
  const availableDCs = useSelector(getAvailableDCsSelector)
  const loadError = useSelector(getDeliveriesLoadErrorSelector)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [enterpriseFilter, setEnterpriseFilter] = useState([])
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)

  const enterpriseById = useMemo(() => Object.fromEntries(enterprises.map((e) => [e.objectId, e])), [enterprises])

  const sorted = useMemo(
    () => [...deliveries].sort((a, b) => new Date(a.deliveryDate) - new Date(b.deliveryDate)),
    [deliveries]
  )

  const filtered = useMemo(
    () => sorted.filter((d) => {
      if (enterpriseById[d.enterpriseId]?.status === "ARCHIVED") return false
      if (enterpriseFilter.length > 0 && !enterpriseFilter.includes(d.enterpriseId)) return false
      if (dateFrom && moment(d.deliveryDate).isBefore(dateFrom, "day")) return false
      if (dateTo && moment(d.deliveryDate).isAfter(dateTo, "day")) return false
      return true
    }),
    [sorted, enterpriseById, enterpriseFilter, dateFrom, dateTo]
  )

  const handleSettings = () => dispatch(showEnterprises())

  const handleSubmit = async (values) => {
    const { created } = await dispatch(createDeliveries(values))
    setIsModalOpen(false)
    dispatch(showDeliveryMenuRecipe(created.map((d) => d.objectId)))
  }

  const handleCardClick = (delivery) => dispatch(showDeliveryMenuRecipe([delivery.objectId]))

  const handleResetFilters = () => { setEnterpriseFilter([]); setDateFrom(null); setDateTo(null) }

  const hasFilter = enterpriseFilter.length > 0 || !!(dateFrom || dateTo)
  const isEmpty = filtered.length === 0

  const emptyState = hasFilter
    ? {
        Icon: FilterAltOffIcon,
        title: "Aucune livraison ne correspond à ces filtres.",
        subtitle: "Élargis la plage ou change d'entreprise pour voir plus de cartes.",
        BtnIcon: RestartAltIcon,
        btnLabel: "Réinitialiser les filtres",
        onClick: handleResetFilters,
      }
    : {
        Icon: LocalShippingIcon,
        title: "Aucune livraison pour l'instant.",
        subtitle: "Créez votre première livraison en cliquant sur le bouton ci-dessous.",
        BtnIcon: AddIcon,
        btnLabel: "Créer une carte",
        onClick: () => setIsModalOpen(true),
      }

  return (
    <Box className="height100 overflowHidden" sx={{ display: "flex", flexDirection: "column" }}>
      <GenericTopBarHeader
        title={<PageHeaderTitle title="Commandes A/R" icon={<LocalShippingIcon sx={{ color: COLORS.PRIMARY_COLOR }} />} color={COLORS.PRIMARY_COLOR} />}
        rightAction={<IconButton onClick={handleSettings} sx={sx.settingsBtn}><SettingsIcon /></IconButton>}
      />

      <DeliveryMenusFilter
        enterprises={enterprises}
        enterpriseFilter={enterpriseFilter}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onEnterpriseChange={setEnterpriseFilter}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
      />

      {loadError && (
        <Typography color="error" sx={{ px: 3, py: 1, fontSize: 14 }}>
          Impossible de charger les livraisons, veuillez réessayer.
        </Typography>
      )}

      {isEmpty ? (
        <Box className="flexCenter flex1" sx={sx.emptyGlobal} role="status" aria-live="polite">
          <emptyState.Icon sx={sx.emptyGlobalIcon} />
          <Typography sx={sx.emptyGlobalTitle}>{emptyState.title}</Typography>
          <Typography sx={sx.emptyGlobalSubtitle}>{emptyState.subtitle}</Typography>
          <Box component="button" type="button" onClick={emptyState.onClick} className="pointer" sx={sx.emptyGlobalBtn}>
            <emptyState.BtnIcon sx={{ fontSize: 18 }} />
            {emptyState.btnLabel}
          </Box>
        </Box>
      ) : (
        <Box sx={sx.board}>
          {STATUS_COLUMNS.map(({ key, label, dot, rgb, headerBg }) => {
            const cards = filtered.filter((d) => (d.status ?? "DRAFT") === key)
            const columnBg = `linear-gradient(to bottom, rgba(${rgb}, 0.08), rgba(${rgb}, 0.04))`
            return (
              <Box key={key} className="flexColumn stretch overflowHidden" sx={{ ...sx.column, background: columnBg }}>
                <Box className="flexRow center" sx={{ ...sx.columnHeader, bgcolor: headerBg }}>
                  <Box className="shrink0" sx={{ ...sx.columnDot, bgcolor: dot }} />
                  <Typography sx={sx.columnTitle}>{label} ({cards.length})</Typography>
                </Box>
                <Box className="flexColumn stretch flex1" sx={sx.columnCards}>
                  {cards.map((d) => {
                    const enterprise = enterpriseById[d.enterpriseId]
                    const enterpriseName = enterprise?.name ?? "—"
                    const enterpriseCode = enterprise?.code ?? null
                    const dayLabel = d.deliveryDate ? moment(d.deliveryDate).format("dddd D MMMM") : "—"
                    return (
                      <Box key={d.objectId} className="pointer flexRow center" sx={sx.card} onClick={() => handleCardClick(d)}>
                        <EnterpriseBadge enterprise={enterprise} size={46} />
                        <Box className="flex1" sx={sx.cardBody}>
                          <Typography className="ellipsis" sx={sx.cardName}>
                            {enterpriseName}
                            {enterpriseCode && <Box component="span" sx={sx.cardRef}> · {enterpriseCode}</Box>}
                            {d.dcCode && <Box component="span" sx={sx.cardRef}> · {d.dcCode}</Box>}
                          </Typography>
                          <Box className="flexRow center" sx={sx.cardSub}>
                            <CalendarTodayOutlinedIcon sx={sx.cardSubIcon} />
                            <Typography sx={sx.cardSubText}>{dayLabel}</Typography>
                          </Box>
                        </Box>
                        <ChevronRightIcon sx={sx.cardChevron} />
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      <Box sx={sx.fab}>
        <Fab color="primary" onClick={() => setIsModalOpen(true)}><AddIcon /></Fab>
      </Box>

      <DeliveryMenuForm
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        enterprises={enterprises}
        availableDCs={availableDCs}
        onGoToSettings={handleSettings}
      />
    </Box>
  )
}

const sx = {
  settingsBtn: { border: "1px solid", borderColor: COLORS.DEFAULT_GREY, borderRadius: "6px" },
  board: {
    flex: 1, minHeight: 0,
    display: "grid", gridTemplateColumns: "repeat(4, minmax(180px, 1fr))",
    gap: "clamp(8px, 1vw, 16px)", overflow: "auto", p: 2,
  },
  column: { border: "1px solid", borderColor: COLORS.DEFAULT_GREY, borderRadius: "8px" },
  columnHeader: { gap: "8px", px: 2, py: 1.5, borderBottom: "1px solid", borderColor: COLORS.DEFAULT_GREY },
  columnDot: { width: 10, height: 10, borderRadius: "50%" },
  columnTitle: { fontSize: 15, fontWeight: 600, color: COLORS.BLACK },
  columnCards: { gap: "8px", p: 2, overflowY: "auto", minHeight: 0 },
  card: {
    bgcolor: COLORS.WHITE, border: "1px solid", borderColor: COLORS.DEFAULT_GREY, borderRadius: "8px",
    p: "12px 10px 12px 12px", gap: "12px",
    transition: "box-shadow 150ms ease, transform 150ms ease", boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
  },
  cardBody: { minWidth: 0 },
  cardName: { fontSize: 18, fontWeight: 700, color: COLORS.BLACK, lineHeight: 1.2 },
  cardRef: { fontWeight: 400, color: COLORS.DARK_GREY, fontSize: 14 },
  cardSub: { gap: "5px", mt: "8px" },
  cardSubIcon: { fontSize: 18, color: COLORS.DISABLED, flexShrink: 0 },
  cardSubText: { fontSize: 14, color: COLORS.DARK_GREY, lineHeight: 1 },
  cardChevron: { fontSize: 24, color: COLORS.DISABLED, flexShrink: 0, alignSelf: "flex-start" },
  emptyGlobal: { gap: "12px", p: 4, textAlign: "center" },
  emptyGlobalIcon: { fontSize: 52, color: COLORS.DISABLED },
  emptyGlobalTitle: { fontSize: 17, fontWeight: 600, color: COLORS.BLACK },
  emptyGlobalSubtitle: { fontSize: 14, color: COLORS.DARK_GREY, maxWidth: 360, lineHeight: 1.5 },
  emptyGlobalBtn: { mt: "4px", display: "inline-flex", alignItems: "center", gap: "6px", height: 36, px: "16px", border: "none", borderRadius: "6px", bgcolor: COLORS.PRIMARY_COLOR, color: COLORS.WHITE, fontFamily: "inherit", fontSize: 14, fontWeight: 500, transition: "filter 150ms", "&:hover": { filter: "brightness(0.92)" } },
  fab: { position: "fixed", bottom: 32, marginLeft: "32px", width: "fit-content" },
}

export default DeliveryMenus
